import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useT } from '../i18n';
import DesignerProfileFields from './DesignerProfileFields';
import {
  USER_TYPES, emptyDesignerProfile, validateDesignerProfile,
} from '../constants/designer';
import { getUserDocument, updateDesignerProfile } from '../firebase/userService';

// ── DESIGNER PROFILE (Settings) ──────────────────────────────────────────────
// The post-signup home for the same fields the registration step collects, and
// the ONLY path to a designer account for anyone who signed up with Google
// (OAuth skips the type step entirely) or who picked Customer and changed their
// mind. Reuses DesignerProfileFields verbatim, so what's accepted here and what
// was accepted at signup can never diverge.
export default function DesignerSettingsCard() {
  const { user, refreshProfile, syncAuthName } = useAuth();
  const { t } = useT();

  const [loading, setLoading]   = useState(true);
  const [isDesigner, setIsDesigner] = useState(false);
  const [profile, setProfile]   = useState(() => ({ ...emptyDesignerProfile(), avatarUrl: '' }));
  const [errors, setErrors]     = useState({});
  const [saving, setSaving]     = useState(false);
  const [toast, setToast]       = useState(null);
  // Customers only: whether they have opened the become-a-designer form.
  // Designers never see the collapsed state — the form IS their profile.
  const [expanded, setExpanded] = useState(false);

  // Load once per user. A designer's saved profile is merged over the empty
  // shape rather than replacing it, so a document written before a field
  // existed still yields a complete, renderable form instead of undefined
  // values turning controlled inputs into uncontrolled ones.
  useEffect(() => {
    let alive = true;
    (async () => {
      if (!user?.uid) return;
      try {
        const doc = await getUserDocument(user.uid);
        if (!alive) return;
        const designer = doc?.userType === USER_TYPES.DESIGNER;
        setIsDesigner(designer);
        setProfile({
          ...emptyDesignerProfile(doc?.fullName || user.displayName || ''),
          ...(doc?.designer || {}),
          // Stored as null when unset; the number input needs ''.
          experienceYears: doc?.designer?.experienceYears ?? '',
          // A saved profile with no rows must still render one blank row.
          websites: doc?.designer?.websites?.length ? doc.designer.websites : [''],
          contacts: doc?.designer?.contacts?.length
            ? doc.designer.contacts : [{ type: 'link', value: '' }],
          avatarUrl: doc?.avatarUrl || '',
        });
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [user?.uid, user?.displayName]);

  const showToast = useCallback((msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 4000);
  }, []);

  async function handleSave(e) {
    e.preventDefault();
    const errs = validateDesignerProfile(profile);
    setErrors(errs);
    if (Object.keys(errs).length) return;

    setSaving(true);
    try {
      const { published } = await updateDesignerProfile(user.uid, profile, profile.avatarUrl);
      setIsDesigner(true);
      // Keep Firebase Auth in step: the header reads displayName from there, so
      // without this the name saved here would not appear until re-login.
      await syncAuthName(profile.fullName);
      // Repull the shared profile so the header avatar/name update without a reload.
      await refreshProfile();
      // The profile IS saved either way; `published` only says whether the
      // public search card went through, so don't call this a failure.
      showToast(published ? t('ds_saved') : t('ds_saved_unpublished'), published ? 'success' : 'error');
    } catch (err) {
      showToast(err?.message || 'Save failed', 'error');
    } finally {
      setSaving(false);
    }
  }

  if (loading) return null;

  return (
    <div className="set-card" style={{ marginTop: 20 }}>
      <div className="set-identity-name" style={{ marginBottom: 4 }}>
        {isDesigner ? t('ds_section_title') : t('ds_become')}
      </div>
      <p className="af-subtitle" style={{ margin: '0 0 6px' }}>
        {isDesigner ? t('ds_section_sub') : t('ds_become_desc')}
      </p>
      {/* The account email — a designer no longer sees the account card above
          (it edited the same name field), so it surfaces here instead. */}
      {isDesigner && user?.email && (
        <p className="dash-user-email" style={{ margin: '0 0 18px' }}>{user.email}</p>
      )}

      {/* A CUSTOMER sees only the invitation until they accept it. Rendered
          inline, this form repeated the picture, the name and the location that
          the account card above already owns — three fields shown twice on one
          page, each pair writing the same document. Behind a button, a
          customer's settings are just their own three fields, and the route to
          a designer account (the only one Google sign-ups ever get) is intact. */}
      {!isDesigner && !expanded ? (
        <button
          type="button"
          className="af-btn set-save-btn ds-become-btn"
          onClick={() => setExpanded(true)}
        >{t('ds_become')}</button>
      ) : (
      <form onSubmit={handleSave} noValidate>
        <DesignerProfileFields
          value={profile}
          onChange={p => { setProfile(p); setErrors({}); }}
          errors={errors}
          disabled={saving}
          // The settings card is the wide layout these fields were designed
          // against — see Settings' `set-main--wide`.
          roomy
        />
        {/* Button and confirmation centred together as one block. The message
            sits directly under the button that caused it — at the top of the
            card it was a full form's scroll away from the Save just pressed,
            which on a form this tall could be off-screen entirely, making a
            successful save look like nothing had happened. */}
        <div className="ds-actions">
          <button className="af-btn set-save-btn" type="submit" disabled={saving}>
            {saving && <span className="af-spinner" />}
            {saving ? t('set_saving') : t('ds_save')}
          </button>

          {toast && (
            <div className={`af-toast af-toast--${toast.type} ds-toast`} role="status" aria-live="polite">
              <span className="af-toast__icon">{toast.type === 'error' ? '⚠' : '✓'}</span>
              {toast.msg}
            </div>
          )}
        </div>
      </form>
      )}
    </div>
  );
}
